import styled from "styled-components";
import {
  CommonMessage,
  CommonGameEventItem,
  CommonMomentSummary,
  FunctionCallResult,
  Source,
} from "../../types";
import { isGameEvent } from "../../util/momentEventItem";
import { ReactNode } from "react";
import { AgencyTags, Channels } from "@bagel-web/components";
import ContentMessage from "./ContentMessage";
import RawMessage from "./RawMessage";
import KVMessage from "./KVMessage";
import FnCallMessage from "./FnCallMessage";
import PuppeteerMessage from "./PuppeteerMessage";
import KVDelTreeMessage from "./KVDelTreeMessage";
import DisplayFnResultIcon from "./FnResultIcon";
import { useAgentViewPreferences } from "../../agent/viewPreferences/agentViewPreferences";
import { kvValueDisplayText } from "../../agent/stateComponents/kvStore/kvStoreNodes";

export const EventBody = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const { showCompactStateContent } = useAgentViewPreferences();
  return (
    <span
      className={`font-monospace ${showCompactStateContent ? "wrap" : "pre-wrap"} ${className}`}
    >
      {children}
    </span>
  );
};

export const EventTableRow = styled.tr<{ $backgroundColor?: string }>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border: 5px solid var(--bs-body-bg);
  font-size: 0.875rem;
  > td {
    padding: 5px;
  }
`;

// Fixed (nowrap guarantees single-line labels fit) so sticky moment headers
// can pin directly below the column headers.
export const MESSAGE_TABLE_HEADER_HEIGHT = "40px";

// box-shadow stands in for a bottom border, which would scroll away with
// border-collapse on a sticky cell.
const MessageTableHeaderCell = styled.th`
  position: sticky;
  /* Stick to the visible top of Page.Body's scrollport, not its content edge,
     so scrolled rows aren't visible in the padding band above the header. */
  top: calc(-1 * var(--aa-page-body-padding-top, 0px));
  z-index: 2;
  height: ${MESSAGE_TABLE_HEADER_HEIGHT};
  background-color: var(--bs-body-bg);
  box-shadow: inset 0 -1px 0 var(--aa-border-subtle);
  font-size: 0.875rem;
  white-space: nowrap;
`;

// The Channels and Tags columns are omitted entirely when nothing in the
// loaded data populates them.
export interface MessageTableColumnVisibility {
  showChannels: boolean;
  showAgencyTags: boolean;
}

export const getMessageTableColumnVisibility = (
  moments: Array<CommonMomentSummary>
): MessageTableColumnVisibility => {
  const gameEvents = moments.flatMap((moment) =>
    moment.event_wrappers.filter(isGameEvent)
  );
  const hasValues = (field: "channels" | "agency_tags"): boolean =>
    gameEvents.some((eventItem) => {
      const values = eventItem.event[field];
      return (values?.length ?? 0) > 0;
    });
  return {
    showChannels: hasValues("channels"),
    showAgencyTags: hasValues("agency_tags"),
  };
};

export function MessageTableHeader({
  columnVisibility,
}: {
  columnVisibility: MessageTableColumnVisibility;
}) {
  return (
    <thead>
      <tr>
        <MessageTableHeaderCell className="p-2 text-end">
          {/* Source */}
        </MessageTableHeaderCell>
        <MessageTableHeaderCell className="p-2">Sender</MessageTableHeaderCell>
        {columnVisibility.showChannels && (
          <MessageTableHeaderCell className="p-2">
            Channels
          </MessageTableHeaderCell>
        )}
        {columnVisibility.showAgencyTags && (
          <MessageTableHeaderCell className="p-2">Tags</MessageTableHeaderCell>
        )}
        <MessageTableHeaderCell className="p-2">
          Message Type
        </MessageTableHeaderCell>
        <MessageTableHeaderCell className="p-2 text-no-wrap">
          {/* Function Result */}
        </MessageTableHeaderCell>
        <MessageTableHeaderCell className="p-2">
          Message Body
        </MessageTableHeaderCell>
      </tr>
    </thead>
  );
}

export function DisplaySource({ source }: { source?: Source }) {
  if (source === "user") {
    return <i className="bi-controller" />;
  } else if (source === "assistant") {
    return <i className="bi-robot" />;
  } else if (source === "system") {
    return <i className="bi-braces" />;
  } else if (source === "function") {
    return <i className="bi-exclamation" />;
  }
}

function HighlightableEventRow({
  children,
  fnCallId,
  highlightedFnCallId,
  onHighlightFnCallId,
}: {
  children: ReactNode;
  fnCallId: string | null;
  highlightedFnCallId: string | null;
  onHighlightFnCallId?: (fnCallId: string | null) => void;
}) {
  const isHighlighted = highlightedFnCallId && highlightedFnCallId === fnCallId;
  const background = isHighlighted
    ? "var(--aa-highlight-bg)"
    : "var(--aa-surface-bg)";

  return (
    <EventTableRow
      data-testid={`game-event-row`}
      $backgroundColor={background}
      onMouseEnter={() => fnCallId && onHighlightFnCallId?.(fnCallId)}
      onMouseLeave={() => onHighlightFnCallId?.(null)}
    >
      {children}
    </EventTableRow>
  );
}

export interface MessageComponentProps<T extends CommonMessage> {
  message: T;
  moment?: CommonMomentSummary;
  textFilter?: string;
}

export const getMessageSearchText = (message: CommonMessage): string => {
  const parts: Array<string> = [];

  switch (message.message_type) {
    case "ContentMessage":
    case "ImportantContentEventMessage":
      if (message.content) parts.push(message.content);
      break;
    case "KVMessage":
      if (message.key) parts.push(message.key);
      if (message.value != null) parts.push(kvValueDisplayText(message.value));
      break;
    case "KVDelTreeMessage":
      if (message.key) parts.push(message.key);
      break;
    case "FnCallMessage":
      if (message.function_call?.name) parts.push(message.function_call.name);
      if (message.function_call?.args)
        parts.push(JSON.stringify(message.function_call.args));
      break;
    case "PuppeteerMessage":
      if (message.puppet_id) parts.push(message.puppet_id);
      message.contents?.forEach((nested) =>
        parts.push(getMessageSearchText(nested))
      );
      break;
    default:
      parts.push(JSON.stringify(message));
  }

  return parts.join(" ").toLowerCase();
};

export type MessageComponent<T extends CommonMessage> = React.FC<
  MessageComponentProps<T>
>;

export const getMessageComponent = (
  messageType?: string
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
MessageComponent<any> => {
  switch (messageType) {
    case "ContentMessage":
    case "ImportantContentEventMessage":
      return ContentMessage;
    case "KVMessage":
      return KVMessage;
    case "KVDelTreeMessage":
      return KVDelTreeMessage;
    case "FnCallMessage":
      return FnCallMessage;
    case "PuppeteerMessage":
      return PuppeteerMessage;
    default:
      return RawMessage;
  }
};

export const getFnCallId = (message: CommonMessage): string | null => {
  switch (message.message_type) {
    case "ContentMessage":
      return message.function_call?.id || null;
    case "FnCallMessage":
      return message.function_call_id || null;
    default:
      return null;
  }
};

export const getResultType = (
  message: CommonMessage
): FunctionCallResult | null => {
  switch (message.message_type) {
    case "ContentMessage":
      return message.function_call?.type || null;
    default:
      return null;
  }
};

export function GameEventRow({
  gameEventItem,
  moment,
  highlightedFnCallId,
  onHighlightFnCallId,
  textFilter,
  columnVisibility,
}: {
  gameEventItem: CommonGameEventItem;
  moment?: CommonMomentSummary;
  highlightedFnCallId: string | null;
  onHighlightFnCallId?: (fnCallId: string | null) => void;
  textFilter?: string;
  columnVisibility: MessageTableColumnVisibility;
}) {
  const message = gameEventItem.event;

  const Message = getMessageComponent(message.message_type);
  const fnCallId: string | null = getFnCallId(message);
  const resultType: FunctionCallResult | null = getResultType(message);
  const sender = message.sender;

  return (
    <HighlightableEventRow
      fnCallId={fnCallId}
      highlightedFnCallId={highlightedFnCallId}
      onHighlightFnCallId={onHighlightFnCallId}
    >
      <td align="right" className="p-2">
        <DisplaySource source={message.source} />
      </td>
      <td width="120px" className="p-2 font-monospace">
        {sender ? sender : "(no sender)"}
      </td>
      {columnVisibility.showChannels && (
        <td className="p-2">
          <Channels channels={message.channels} />
        </td>
      )}
      {columnVisibility.showAgencyTags && (
        <td className="p-2">
          <AgencyTags tags={message.agency_tags} />
        </td>
      )}
      <td className="p-2 font-monospace">
        {message.message_type}
        {message.ignorable && <div>ignorable</div>}
      </td>
      <td>
        {resultType && (
          <DisplayFnResultIcon resultType={resultType} fnCallId={fnCallId} />
        )}
      </td>
      <td className={`p-2`}>
        <Message message={message} moment={moment} textFilter={textFilter} />
      </td>
    </HighlightableEventRow>
  );
}
